import * as React from 'react'
import { Search, X } from 'lucide-react'

import { cn } from '../../../lib/utils'
import { Select } from '../../ui/Select'
import type { FilterDef, TableState } from './types'

interface DataTableToolbarProps {
  state: TableState
  onStateChange: (patch: Partial<TableState>) => void
  searchPlaceholder?: string
  filters?: readonly FilterDef[]
  /** Buttons pinned to the right of the toolbar. */
  actions?: React.ReactNode
  className?: string
}

/** Debounce lives here rather than in a dependency: it is eight lines. */
function useDebouncedCallback(callback: (value: string) => void, delay: number) {
  const latest = React.useRef(callback)
  React.useEffect(() => {
    latest.current = callback
  })

  const timer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  React.useEffect(() => () => clearTimeout(timer.current), [])

  return React.useCallback(
    (value: string) => {
      clearTimeout(timer.current)
      timer.current = setTimeout(() => latest.current(value), delay)
    },
    [delay],
  )
}

function DataTableToolbar({
  state,
  onStateChange,
  searchPlaceholder = 'Search…',
  filters,
  actions,
  className,
}: DataTableToolbarProps) {
  const [draft, setDraft] = React.useState(state.search)
  const commit = useDebouncedCallback((value) => onStateChange({ search: value }), 300)

  // Keep the box in step when search is reset from outside (browser back, a
  // cleared URL). Adjusting during render rather than in an effect avoids the
  // extra commit — this is React's documented "derive state from props" escape
  // hatch, and it converges because it only fires when the two disagree.
  const [lastExternal, setLastExternal] = React.useState(state.search)
  if (state.search !== lastExternal) {
    setLastExternal(state.search)
    setDraft(state.search)
  }

  const handleChange = (value: string) => {
    setDraft(value)
    commit(value)
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <div
        className={cn(
          'border-border bg-bg-elev text-fg-muted flex h-9 w-72 max-w-full items-center gap-2 rounded-md border px-2.5 transition-colors duration-100 ease-out',
          'focus-within:outline-accent focus-within:outline-2 focus-within:outline-offset-1',
        )}
      >
        <Search className="size-3 shrink-0" />
        <input
          type="search"
          value={draft}
          onChange={(event) => handleChange(event.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          autoComplete="off"
          spellCheck={false}
          className="text-fg placeholder:text-fg-subtle min-w-0 flex-1 border-0 bg-transparent text-base outline-none [&::-webkit-search-cancel-button]:hidden"
        />
        {draft && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => handleChange('')}
            className="text-fg-subtle hover:text-fg focus-visible:outline-accent shrink-0 rounded-sm transition-colors duration-100 ease-out outline-none focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            <X className="size-3" />
          </button>
        )}
      </div>

      {filters?.map((filter) => (
        <Select
          key={filter.key}
          aria-label={filter.label}
          options={filter.options}
          value={state.filters[filter.key] ?? ''}
          onValueChange={(value) =>
            onStateChange({ filters: { ...state.filters, [filter.key]: value } })
          }
          placeholder={filter.label}
          triggerClassName="min-w-36"
        />
      ))}

      {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
    </div>
  )
}

export { DataTableToolbar, type DataTableToolbarProps }
